// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87b, owner 2026-10-21: the enrollment review drawer's OPTIONAL "Add Rx". Lists the patient's PA-approved
// prescriptions of the same drug that are on no enrollment yet; the operator picks one and says why. Adding sends
// the enrollment back to Clinical Review and re-checks its baseline labs (A2). Nothing is ever added on its own.

import { useState } from 'react'
import AppButton from '../../components/ui/AppButton'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { SelectField } from '../../components/ui/FormFields'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import { isRefusedAttach, useAttachRx, useEligibleRx, useRefreshTherapyRx } from '../../hooks/queries/carerxCombine'

export default function AddRxButton({ therapyId, therapyRef, onDone }: {
  therapyId: string
  therapyRef: string
  onDone: (result: { rxNumber: string; missingLabs: string[] }) => void
}) {
  const [open, setOpen] = useState(false)
  const [rx, setRx] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const eligible = useEligibleRx(therapyId, open)
  const attach = useAttachRx(() => undefined)
  const refreshTherapyRx = useRefreshTherapyRx()
  // Stress test 2026-10-21: a refused add (409/422) was a stale choice. The picker list reloads at once; the
  // enrollment itself (it may have closed or expired) reloads when the dialog closes.
  const [refused, setRefused] = useState(false)
  const items = eligible.data?.items ?? []
  const close = () => {
    setOpen(false); setRx(''); setErr(null)
    if (refused) { setRefused(false); void refreshTherapyRx(therapyId) }
  }
  return (
    <>
      <AppButton size="small" variant="outlined" onClick={() => { setErr(null); setRx(''); setOpen(true) }}>Add Rx</AppButton>
      <ConfirmDialog open={open} title={`Add a prescription to ${therapyRef}`}
        confirmLabel="Add Rx" busyLabel="Adding…" busy={attach.isPending} error={err}
        confirmDisabled={!rx}
        requireReason={{ label: 'Why is it being added?' }}
        body={<>
          {eligible.isPending && <p style={{ margin: 0 }}>Loading the patient&#39;s prescriptions…</p>}
          {eligible.isError && <p role="alert" style={{ margin: 0, color: '#b91c1c' }}>{friendlyError(eligible.error)}</p>}
          {eligible.isSuccess && (!eligible.data.can_add || items.length === 0) && (
            <p style={{ margin: 0 }}>
              {eligible.data.can_add
                ? 'No other PA-approved prescription of this drug is waiting for an enrollment.'
                : 'This enrollment cannot take another prescription (it is closed or expired).'}
            </p>)}
          {eligible.isSuccess && eligible.data.can_add && items.length > 0 && (
            <SelectField size="small" fullWidth label="Prescription" value={rx} onChange={v => setRx(v)}
              options={items.map(i => ({
                value: i.rx_number,
                label: `Rx ${i.rx_number} · ${i.drug_name ?? 'drug'}${i.received_at ? ` · received ${formatUsDate(i.received_at)}` : ''}`,
              }))} />)}
          <p style={{ margin: '10px 0 0', fontSize: 12.5 }}>
            Adding a prescription sends the enrollment back to <b>Clinical Review</b> and checks its baseline labs again.
          </p>
        </>}
        onCancel={close}
        onConfirm={({ reason }) => attach.mutate({ therapyId, rxNumber: rx, source: 'drawer', reason }, {
          onSuccess: r => { close(); onDone({ rxNumber: r.rx_number, missingLabs: r.missing_labs }) },
          onError: e => { setErr(friendlyError(e)); if (isRefusedAttach(e)) { setRefused(true); setRx('') } },
        })} />
    </>
  )
}
